import React, { FC, useRef } from "react";
import { Container, Stack, Typography } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { motion, useInView } from "framer-motion";
import FooterLinkItem from "./FooterLinkItem";
import "./styles/footer.scss";
import {
  ABOUT_US,
  CAREER_CONNECT,
  CONTACT_US,
  JOBS,
  MEET_OUR_TEAM,
  OUR_SERVICES,
  PRIVACY_POLICY,
  TECHNOLOGIES,
  TERMS_AND_CONDITIONS
} from "../pageRoutes";

// eslint-disable-next-line @typescript-eslint/no-require-imports
const classNames = require("classnames");

const sectionVariants = {
  hidden: { opacity: 0, y: 16 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.08, duration: 0.4 }
  })
};

interface FooterProps {
  hideFooterLinks?: boolean;
}

const Footer: FC<FooterProps> = (props) => {
  const { hideFooterLinks } = props;
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));
  const footerRef = useRef(null);
  const inView = useInView(footerRef, { once: true, margin: "-50px" });

  const footerContainerClasses = classNames("footerContainer", {
    smallScreen: matchMD
  });

  return (
    <Stack className="footer" ref={footerRef}>
      {!hideFooterLinks && (
        <Stack className={footerContainerClasses}>
          {[
            {
              title: "Learn about",
              links: [
                { title: "Who we are", linkUrl: ABOUT_US },
                { title: "Our services", linkUrl: OUR_SERVICES },
                { title: "Our team", linkUrl: MEET_OUR_TEAM }
              ]
            },
            {
              title: "Resources",
              links: [
                { title: "Technologies", linkUrl: TECHNOLOGIES },
                { title: "Career", linkUrl: JOBS },
                {
                  title: "Terms and conditions",
                  linkUrl: TERMS_AND_CONDITIONS
                },
                { title: "Privacy policy", linkUrl: PRIVACY_POLICY }
              ]
            },
            {
              title: "Events",
              links: [{ title: "Career Connect", linkUrl: CAREER_CONNECT }]
            },
            {
              title: "Contact us",
              links: [
                {
                  title: "+250794004770",
                  linkUrl: "+250794004770",
                  isPhone: true
                },
                {
                  title: "tech@kivunova.com",
                  linkUrl: "tech@kivunova.com",
                  isEmail: true
                },
                { title: "send us a message", linkUrl: CONTACT_US }
              ]
            }
          ].map((section, i) => (
            <motion.div
              key={section.title}
              custom={i}
              initial="hidden"
              animate={inView ? "visible" : "hidden"}
              variants={sectionVariants}
            >
              <Container className="footerSection">
                <Typography variant="h6" className="footerSectionTitle">
                  {section.title}
                </Typography>
                <Container className="footerSectionContent">
                  {section.links.map((link) => (
                    <FooterLinkItem
                      key={link.linkUrl}
                      title={link.title}
                      linkUrl={link.linkUrl}
                      isPhone={"isPhone" in link && link.isPhone}
                      isEmail={"isEmail" in link && link.isEmail}
                    />
                  ))}
                </Container>
              </Container>
            </motion.div>
          ))}
        </Stack>
      )}
      <motion.section
        className="footerCopyRight"
        initial={{ opacity: 0 }}
        animate={inView ? { opacity: 1 } : { opacity: 0 }}
        transition={{ delay: 0.3 }}
      >
        {`${new Date().getFullYear()} © Kivunova`}
      </motion.section>
    </Stack>
  );
};

export default Footer;
